import { useQuery } from '@apollo/client';
import { IColumn, IconButton, Stack, Text, TooltipHost } from '@fluentui/react';
import { GenericAttachForm } from 'common/components/ActionMenu/Attach/AttachFormModal';
import { AmountTextView } from 'common/components/AmountView/AmountTextView';
import { DocumentDataCallout } from 'common/components/AttachDocumentModal/DocumentDataCallout';
import { DocumentViewModalState } from 'common/components/DocumentList';
import {
  dateConvertions,
  dateFormat,
  getGlobalDateFormat,
} from 'common/utils/dateFormats';
import React, { useCallback, useMemo } from 'react';

import {
  AttachableBatchTransactionDocuments,
  AttachableBatchTransactionDocuments_attachableBatchTransactionDocuments_nodes,
  AttachableBatchTransactionDocumentsVariables,
} from '../../__generated__/AttachableBatchTransactionDocuments';
import { BatchTransactionAvailableDocumentTypes } from '../../__generated__/BatchTransactionAvailableDocumentTypes';
import { columns } from './column.data';
import { useStyles } from './index.styles';
import { toOrderByVariable } from './utils';

import ATTACHABLE_BATCH_TRANSACTION_DOCUMENTS from '../../AttachableBatchTransactionDocuments.graphql';
import BATCH_TRANSACTION_AVAILABLE_DOCUMENT_TYPES from '../../BatchTransactionAvailableDocumentTypes.graphql';
export type AttachableBatchDocumentsType =
  AttachableBatchTransactionDocuments_attachableBatchTransactionDocuments_nodes;

export interface AttachedBatchDocuments {
  attachedDocuments: AttachableBatchDocumentsType[];
}

export interface AttachBatchDocumentsProps {
  onDocumentsAttached: (
    attachedDocumentsData: AttachableBatchDocumentsType[]
  ) => void;
  prevSelectedDocuments?: AttachableBatchDocumentsType[];
}

export const AttachBatchDocumentsModal: React.FC<AttachBatchDocumentsProps> = ({
  prevSelectedDocuments,
  onDocumentsAttached,
}) => {
  const styles = useStyles();

  const { data: documentTypesData } =
    useQuery<BatchTransactionAvailableDocumentTypes>(
      BATCH_TRANSACTION_AVAILABLE_DOCUMENT_TYPES,
      {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-only',
      }
    );

  const getDocumentTypeOptions = useCallback(
    (data: BatchTransactionAvailableDocumentTypes | undefined) => {
      return (
        data?.batchTransactionAvailableDocumentTypes?.nodes.map((doctype) => ({
          key: doctype.id,
          text: doctype.documentType || '',
        })) || []
      );
    },
    []
  );

  const transformData = useMemo(
    () => (data: AttachableBatchTransactionDocuments | undefined) => {
      const documents = data?.attachableBatchTransactionDocuments?.nodes.map(
        (document) =>
          ({
            ...document,
            available: document.documentAppliedAmounts?.remainingTotal,
            isoCode: document.currency?.isoCode,
          } as AttachableBatchDocumentsType)
      );
      if (prevSelectedDocuments?.length && documents?.length) {
        const filteredArray = documents.filter(
          ({ id: id1 }) =>
            !prevSelectedDocuments.some(({ id: id2 }) => id2 === id1)
        );
        return filteredArray;
      }
      return documents;
    },
    [prevSelectedDocuments]
  );

  const onRenderItemColumn = useCallback(
    (
      item?: AttachableBatchDocumentsType,
      _index?: number,
      column?: IColumn,
      openDocumentViewModal?: (state: DocumentViewModalState) => void
    ) => {
      if (!item || !column) return undefined;
      const fieldContent = item[
        column.fieldName as keyof AttachableBatchDocumentsType
      ] as string | null;

      switch (column.key) {
        case 'indexAmount':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <AmountTextView
                value={fieldContent}
                className={styles.contentColumnAlignRight}
              />
            </Stack>
          );
        case '_documentPoolName':
          return (
            <Stack
              horizontal
              className={styles.columnHeight}
              verticalAlign="center"
              tokens={{ childrenGap: 5 }}
            >
              <Text>{fieldContent}</Text>
            </Stack>
          );
        case 'fileReference':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <DocumentDataCallout item={item} />
            </Stack>
          );
        case '_uploadDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>{getGlobalDateFormat(item._uploadDate!)}</Text>
            </Stack>
          );
        case 'view':
          const viewDocumentVisible =
            item._isProtected! || item._fileViewer !== 'browser';
          return (
            <Stack
              className={styles.columnHeight}
              tokens={{ childrenGap: 10 }}
              horizontal
              verticalAlign="center"
            >
              <TooltipHost content="View" id="tooltipId">
                <IconButton
                  disabled={viewDocumentVisible}
                  iconProps={{ iconName: 'View' }}
                  onClick={() =>
                    openDocumentViewModal?.({
                      isOpen: true,
                      title: item.fileReference,
                      entityDocumentId: item.id,
                      _fileType: item._fileType!,
                    })
                  }
                />
              </TooltipHost>
            </Stack>
          );
        case 'indexTransactionDate':
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              {item.indexTransactionDate && (
                <Text>
                  {dateFormat(dateConvertions(item.indexTransactionDate))}
                </Text>
              )}
            </Stack>
          );
        default:
          return (
            <Stack className={styles.columnHeight} verticalAlign="center">
              <Text>{fieldContent}</Text>
            </Stack>
          );
      }
    },
    [styles]
  );

  const getMutationInput = useCallback(
    (selectedItems: AttachableBatchDocumentsType[]) => {
      return selectedItems as AttachableBatchDocumentsType[];
    },
    []
  );

  const onAttachedSuccessfully = useCallback(
    (selectedItems?: AttachableBatchDocumentsType[]) => {
      if (!selectedItems) return;
      const attachedDocuments = selectedItems.map(
        (item) => ({ ...item } as AttachableBatchDocumentsType)
      );
      onDocumentsAttached(attachedDocuments);
    },
    [onDocumentsAttached]
  );
  return (
    <GenericAttachForm<
      AttachableBatchDocumentsType,
      AttachableBatchTransactionDocuments,
      AttachableBatchTransactionDocumentsVariables,
      unknown,
      AttachableBatchDocumentsType[],
      BatchTransactionAvailableDocumentTypes
    >
      entity={{ id: null }} // No entityId required
      availableDocumentTypes={documentTypesData}
      attachableDocumentsQuery={ATTACHABLE_BATCH_TRANSACTION_DOCUMENTS}
      detailsQuery={ATTACHABLE_BATCH_TRANSACTION_DOCUMENTS} // Dummy, as not used
      columns={columns}
      modalWidth={1450}
      getDocumentTypeOptions={getDocumentTypeOptions}
      transformData={transformData}
      getMutationInput={getMutationInput}
      onRenderItemColumn={onRenderItemColumn}
      initialQueryVariables={{}}
      toOrderByVariable={toOrderByVariable}
      onAttachedSuccessfully={onAttachedSuccessfully}
      buttonRightAligned
      buttonPaddingTop={20}
    />
  );
};
